import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import SignupState from './SignupState';
import { FooterMessage } from '../FooterMessage';
import RecaptchaText from '../../Layout/Fields/RecaptchaText';

export const SignupPreviousButton = (props) => {
  const { t } = useTranslation('buttons');

  const { onClick, buttonClass } = props;
  return (
    <div className="level-left step-action is-4-tablet is-6-mobile">
      <button
        type="button"
        className={`button is-white is-outlined button-modal-prev is-medium is-small-mobile is-full-width is-size-6-mobile ${buttonClass}`}
        data-nav="previous"
        onClick={onClick}
      >
        <i className="fa fa-arrow-left" />
        &nbsp;&nbsp;
        { t('previous') }
      </button>
    </div>
  );
};

SignupPreviousButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  buttonClass: PropTypes.string.isRequired,
};

export const SignupNextButton = ({ onClick, buttonClass, showError }) => {
  const { t: tButtons } = useTranslation('buttons');
  const { t: tErrors } = useTranslation('errors');

  return (
    <div className="level-right step-action is-6-mobile is-4-tablet">
      <div className="next-holder">
        <button
          type="submit"
          className={`button is-success button-modal-next is-medium is-small-mobile is-full-width is-size-6-mobile ${buttonClass} ${showError ? 'is-danger' : ''}`}
          data-nav="next"
          onClick={onClick}
        >
          { tButtons('next') }
          &nbsp;&nbsp;
          <i className="fa fa-arrow-right" />
        </button>

        {showError && (
          <p className="next-error">
            <i className="fas fa-exclamation-triangle" />
            &nbsp;
            {tErrors('errors:check_required_fields_for_errors')}
          </p>
        )}
      </div>
    </div>
  );
};

SignupNextButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  buttonClass: PropTypes.string.isRequired,
  showError: PropTypes.bool.isRequired,
};

export const SignupCloseButton = (props) => {
  const { t } = useTranslation('buttons');
  const { closeButton } = props;

  return (
    <div className="columns is-mobile is-centered steps-actions-completed">
      <div className="column step-action is-9">
        <button
          type="button"
          className="button button-modal-close has-background-light is-medium is-small-mobile is-full-width is-uppercase"
          onClick={closeButton}
        >
          <i className="icon-close" />
          &nbsp;&nbsp;
          { t('close') }
          &nbsp;&nbsp;
        </button>
      </div>
    </div>
  );
};

SignupCloseButton.propTypes = {
  closeButton: PropTypes.func.isRequired,
};

class SignupFooter extends Component {
  constructor(props) {
    super(props);
    this.state = { triedNext: false };
  }

  componentDidUpdate(prevProps) {
    const { signupState } = this.props;
    const { triedNext } = this.state;

    if (prevProps.signupState !== signupState && triedNext) {
      this.setState({ triedNext: false });
    }

    if (triedNext && !this.hasValidationErrors()) {
      this.setState({ triedNext: false });
    }
  }

  onNextClick = (e) => {
    const { nextButton } = this.props;
    this.setState({ triedNext: true }, () => {
      nextButton(e);
    });
  };

  buttonsConfiguration = () => {
    const { signupState } = this.props;
    const isDefault = '';
    const isHidden = 'is-hidden';
    const buttonActive = 'is-success';
    const prevButtonActive = 'is-white';
    const prevButtonDisabled = 'is-white is-disabled';
    const config = {
      cancelButtonClass: isHidden,
      previousButtonClass: isHidden,
      nextButtonClass: isHidden,
    };

    switch (signupState) {
      case SignupState.first:
        config.previousButtonClass = prevButtonDisabled;
        config.nextButtonClass = buttonActive;
        break;
      case SignupState.second:
        config.previousButtonClass = prevButtonActive;
        config.nextButtonClass = buttonActive;
        break;
      case SignupState.third:
        config.cancelButtonClass = isDefault;
        break;
      default:
        break;
    }

    return config;
  };

  isFieldInvalid = (field) => {
    if (!field?.validation?.required) return false;
    const baseValid = field.valid === true;
    const inlineOk = field.validation.inline ? field.onlineValid !== false : true;
    return !(baseValid && inlineOk);
  };

  hasValidationErrors = (props = this.props) => {
    const {
      signupState,
      accountDetails,
      personalDetails,
      addressDetails,
      acceptFields,
    } = props;

    const accountFields = accountDetails?.fields || {};
    const personalFields = personalDetails?.fields || {};
    const addressFields = addressDetails?.fields || {};
    const acceptFieldsData = acceptFields?.fields || {};

    if (signupState === SignupState.first) {
      return Object.values(accountFields).some(this.isFieldInvalid);
    }

    if (signupState === SignupState.second) {
      const allFields = { ...personalFields, ...addressFields, ...acceptFieldsData };
      return Object.values(allFields).some(this.isFieldInvalid);
    }

    return false;
  };

  render() {
    const { signupState, closeButton, previousButton } = this.props;
    const { triedNext } = this.state;
    const showError = triedNext && this.hasValidationErrors();
    let renderedStep = '';

    if (signupState !== SignupState.fourth && signupState !== SignupState.error) {
      const config = this.buttonsConfiguration();
      renderedStep = (
        <div className="level is-mobile is-centered steps-actions">
          <SignupPreviousButton
            buttonClass={config.previousButtonClass}
            onClick={previousButton}
          />
          <SignupNextButton
            buttonClass={config.nextButtonClass}
            onClick={this.onNextClick}
            showError={showError}
          />
        </div>
      );
    } else {
      renderedStep = <SignupCloseButton closeButton={closeButton} />;
    }

    return (
      <>
        <footer className="modal-card-foot">
          {renderedStep}
        </footer>
        <FooterMessage>
          <RecaptchaText />
        </FooterMessage>
      </>
    );
  }
}

SignupFooter.propTypes = {
  signupState: PropTypes.instanceOf(SignupState).isRequired,
  closeButton: PropTypes.func.isRequired,
  previousButton: PropTypes.func.isRequired,
  nextButton: PropTypes.func.isRequired,
  accountDetails: PropTypes.shape({ fields: PropTypes.shape({}) }),
  personalDetails: PropTypes.shape({ fields: PropTypes.shape({}) }),
  addressDetails: PropTypes.shape({ fields: PropTypes.shape({}) }),
  acceptFields: PropTypes.shape({ fields: PropTypes.shape({}) }),
};

SignupFooter.defaultProps = {
  accountDetails: { fields: {} },
  personalDetails: { fields: {} },
  addressDetails: { fields: {} },
  acceptFields: { fields: {} },
};

export default SignupFooter;
